import { CheckCircle2, Loader2, RotateCcw, Send, Trash2 } from 'lucide-react';
import { useEffect, useId, useState } from 'react';
import { toast } from 'sonner';
import { replyAdminTicket, setAdminTicketStatus } from '@/api/admin';
import TicketThread from '@/components/support/TicketThread';
import { errorMessage, inboxLabel, inboxPillStyle } from './ticketInbox';

/**
 * One ticket's conversation for the admin: reply (waiting on the member, or resolve with it), resolve
 * or reopen without replying, or delete. `onChanged` reloads the inbox after any change.
 */
const AdminTicketModal = ({ ticket, onClose, onChanged, onDelete }) => {
  const [reply, setReply] = useState('');
  const [resolve, setResolve] = useState(false);
  const [busy, setBusy] = useState(false);
  const uid = useId();

  // biome-ignore lint/correctness/useExhaustiveDependencies: ticket_id is the trigger — a different ticket starts a blank reply
  useEffect(() => {
    setReply('');
    setResolve(false);
  }, [ticket?.ticket_id]);

  if (!ticket) return null;
  const resolved = ticket.inbox_status === 'resolved';

  const run = async (work, success) => {
    setBusy(true);
    try {
      await work();
      toast.success(success);
      onChanged();
      onClose();
    } catch (err) {
      toast.error(`Failed: ${errorMessage(err)}`);
    } finally {
      setBusy(false);
    }
  };

  const sendReply = () => {
    if (!reply.trim()) {
      toast.error('Reply cannot be empty');
      return;
    }
    run(() => replyAdminTicket(ticket.ticket_id, reply.trim(), resolve), resolve ? 'Reply sent and ticket resolved' : 'Reply sent — waiting on member');
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4" role="dialog" aria-modal="true" data-testid="ticket-modal">
      <button type="button" aria-label="Close" tabIndex={-1} className="absolute inset-0 bg-black/75 backdrop-blur-md" onClick={() => !busy && onClose()} />
      <div className="relative sc-glass-strong rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="px-6 py-5 border-b border-[rgba(255,182,18,0.18)] flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#FFC20E]">Member Ticket</div>
            <div className="font-display font-bold text-xl mt-1 break-words">{ticket.subject}</div>
            <div className="text-xs text-[var(--sc-text-mute)] mt-0.5 break-all">
              {ticket.username || ticket.user_id} · {ticket.email || 'no email'}
            </div>
          </div>
          <span
            className="shrink-0 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold tracking-[0.18em] uppercase"
            style={inboxPillStyle(ticket.inbox_status)}
          >
            {inboxLabel(ticket.inbox_status)}
          </span>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          <TicketThread messages={ticket.messages} viewer="admin" memberName={ticket.username} />
          <div>
            <label htmlFor={`${uid}-reply`} className="block text-[11px] uppercase tracking-[0.18em] text-[var(--sc-text-mute)] mb-1.5">
              Your Reply
            </label>
            <textarea
              id={`${uid}-reply`}
              className="sc-input"
              rows={5}
              maxLength={4000}
              value={reply}
              onChange={(e) => setReply(e.target.value)}
              placeholder="Type your reply…"
              disabled={busy}
              data-testid="ticket-reply-text"
            />
          </div>
          <label className="inline-flex items-center gap-2 text-[12px] text-[var(--sc-text-dim)]">
            <input
              type="checkbox"
              className="sc-checkbox"
              checked={resolve}
              onChange={(e) => setResolve(e.target.checked)}
              data-testid="ticket-resolve-check"
            />
            Mark ticket as resolved after sending (otherwise it waits on the member)
          </label>
        </div>

        <div className="px-6 py-4 border-t border-[rgba(255,182,18,0.18)] flex items-center justify-between gap-2 flex-wrap bg-[rgba(8,8,9,0.6)]">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => onDelete(ticket)}
              disabled={busy}
              className="inline-flex items-center gap-1.5 rounded-xl px-3 py-2.5 text-[11px] font-bold uppercase tracking-[0.16em] text-[#FF6B6B] border border-[rgba(255,107,107,0.45)] hover:bg-[rgba(255,107,107,0.08)] disabled:opacity-50"
              data-testid="ticket-delete"
            >
              <Trash2 className="w-3.5 h-3.5" />
              Delete
            </button>
            <button
              type="button"
              onClick={() =>
                run(() => setAdminTicketStatus(ticket.ticket_id, resolved ? 'Open' : 'Resolved'), resolved ? 'Ticket reopened' : 'Ticket resolved')
              }
              disabled={busy}
              className="sc-btn-ghost"
              style={{ padding: '10px 14px', fontSize: 11 }}
              data-testid="ticket-toggle-resolved"
            >
              {resolved ? <RotateCcw className="w-3.5 h-3.5" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
              {resolved ? 'Reopen' : 'Mark Resolved'}
            </button>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={busy}
              className="sc-btn-ghost"
              style={{ padding: '10px 14px', fontSize: 12 }}
              data-testid="ticket-cancel"
            >
              Close
            </button>
            <button
              type="button"
              onClick={sendReply}
              disabled={busy || !reply.trim()}
              className="sc-btn-gold"
              style={{ padding: '10px 18px', fontSize: 12 }}
              data-testid="ticket-submit"
            >
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : resolve ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Send className="w-3.5 h-3.5" />}
              {resolve ? 'Send & Resolve' : 'Send Reply'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminTicketModal;
